import React, { createContext, useEffect, useState, useContext } from 'react';

import en from '../locales/en.json';
import nl from '../locales/nl.json';
import logger from '../utils/logger';

const translations = { en, nl };

const TranslationContext = createContext();

export const useTranslation = () => useContext(TranslationContext);

const normalizeLanguage = (lang) => (lang || 'en').toLowerCase();

export const TranslationProvider = ({ children }) => {
  const [language, setLanguage] = useState(localStorage.getItem('preferredLanguage') || 'en');

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const replacePlaceholders = (value, params) => {
    if (typeof value === 'string') {
      // Replace every {key} in the string with params[key]
      return Object.keys(params).reduce((result, param) => {
        const regex = new RegExp(`{${param}}`, 'g');
        return result.replace(regex, params[param]);
      }, value);
    }
    if (Array.isArray(value)) {
      // Apply the same placeholder replacement to each item
      return value.map((item) => replacePlaceholders(item, params));
    }
    return value;
  };

  const translateForLanguage = (lang, key, params = {}) => {
    if (!key || typeof key !== 'string') {
      logger.warn(`Translation key is invalid: ${key}`);
      return key;
    }

    const keys = key.split('.');
    const selectedTranslations = translations[normalizeLanguage(lang)] || translations.en;
    const translation = keys.reduce((acc, k) => acc && acc[k], selectedTranslations) || key;

    return replacePlaceholders(translation, params);
  };

  const translate = (key, params = {}) => translateForLanguage(language, key, params);

  const changeLanguage = (lang) => {
    setLanguage(lang);
    localStorage.setItem('preferredLanguage', lang);
  };

  return (
    <TranslationContext.Provider
      value={{ translate, translateForLanguage, changeLanguage, language }}
    >
      {children}
    </TranslationContext.Provider>
  );
};
